import React, { useEffect, useState, useMemo } from "react";
import { Button, Select, Tag } from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import GetOnHoldTasksApi from "../../api/tasks/GetOnHoldTasksApi";
import CompleteTaskApi from "../../api/tasks/CompleteTaskApi";
import { openNotificationSuccess, openNotificationError } from "../../helpers/NotificationHelpers";
import PageLoader from "../sub/PageLoader";
import moment from "moment";
import ConfirmationModal from "../modal/ConfirmationModal";

const { Option } = Select;

function ResolvedGroup({ group }) {
  const [open, setOpen] = useState(false);
  const { heldTask, deps } = group;

  const isCompleted = heldTask.completionStatus;
  const badgeLabel = isCompleted ? "Resolved" : "Released · Awaiting Completion";
  const badgeBg = isCompleted ? "#dcfce7" : "#fef9c3";
  const badgeColor = isCompleted ? "#166534" : "#92400e";
  const badgeBorder = isCompleted ? "#86efac" : "#fde68a";

  return (
    <div style={{ backgroundColor: "#fff", border: "1px solid #bbf7d0", borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: "#166534", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
        <CheckCircleOutlined style={{ color: "#16a34a" }} />
        {heldTask.name}
        <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, backgroundColor: badgeBg, color: badgeColor, fontWeight: 700, border: `1px solid ${badgeBorder}` }}>{badgeLabel}</span>
      </div>
      {heldTask.ownerName && (
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 2 }}>{heldTask.ownerName} {heldTask.ownerLastName || ""}</div>
      )}
      {heldTask.planTitle && (
        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 2 }}>Plan: {heldTask.planTitle}</div>
      )}
      {heldTask.resolvedOn && (
        <div style={{ fontSize: 11, color: "#94a3b8" }}>
          {isCompleted ? "Resolved" : "Released"} {moment(heldTask.resolvedOn).format("MMM DD")} {heldTask.daysToResolve ? `· ${heldTask.daysToResolve} days` : ""}
        </div>
      )}

      {deps.length > 0 && (
        <div
          onClick={() => setOpen(!open)}
          style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 10, cursor: "pointer", fontSize: 11, fontWeight: 600, color: "#2563eb" }}
        >
          <CaretRightOutlined style={{ fontSize: 9, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
          {deps.length} dependency task{deps.length > 1 ? "s" : ""} resolved
        </div>
      )}

      {open && deps.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {deps.map((dep, i) => (
            <div key={dep.id || i} style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#166534" }}>{dep.name}</div>
                <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, backgroundColor: "#dbeafe", color: "#1e40af", fontWeight: 700, border: "1px solid #93c5fd" }}>Resolved</span>
              </div>
              {dep.ownerName && <div style={{ fontSize: 11, color: "#475569" }}>{dep.ownerName} {dep.ownerLastName || ""}</div>}
              {dep.planTitle && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>Plan: {dep.planTitle}</div>}
              {dep.resolvedOn && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>Resolved {moment(dep.resolvedOn).format("MMM DD")}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DependenciesTab() {
  const [data, setData] = useState({ blocking: [], waiting: [], resolved: [] });
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ visible: false, taskId: null });
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  const userName = positionState?.users?.firstName || "User";
  const userDesignation = positionState?.designations?.abbreviation || positionState?.designations?.name || "";

  const yearOptions = useMemo(() => {
    const current = moment().year();
    return [current - 1, current, current + 1];
  }, []);

  const weekOptions = useMemo(() => {
    const options = [{ value: null, label: "All weeks" }];
    const totalWeeks = moment().year(selectedYear).isoWeeksInYear();
    for (let w = 1; w <= totalWeeks; w++) {
      const start = moment().year(selectedYear).isoWeek(w).startOf("isoWeek").format("DD MMM");
      const end = moment().year(selectedYear).isoWeek(w).endOf("isoWeek").format("DD MMM");
      options.push({ value: w, label: `W${w} (${start} – ${end})` });
    }
    return options;
  }, [selectedYear]);

  // Determine which weeks have active tasks for this user (for highlighting in dropdown)
  const weeksWithData = useMemo(() => {
    const activeWeeks = new Set();
    // Use blocking data — these are MY tasks that are blocking someone
    // Also check waiting — these show tasks I'm waiting on
    const allTaskDates = [];
    (data.blocking || []).forEach(item => {
      (item.myBlockingTaskNames || []).forEach(task => {
        if (task.taskEndDate) allTaskDates.push(moment(task.taskEndDate));
      });
    });
    (data.waiting || []).forEach(item => {
      (item.dependentTasks || []).forEach(task => {
        if (task.taskEndDate) allTaskDates.push(moment(task.taskEndDate));
      });
      // The on-hold task itself
      if (item.daysOnHold) {
        // Approximate: on-hold task's target date
      }
    });
    allTaskDates.forEach(d => {
      if (d.year() === selectedYear) activeWeeks.add(d.isoWeek());
    });
    return activeWeeks;
  }, [data, selectedYear]);

  useEffect(() => {
    if (perfCycleState?.id && positionState?.id) {
      fetchDependencies();
    }
  }, [perfCycleState, positionState]);

  async function fetchDependencies() {
    setLoading(true);
    const response = await GetOnHoldTasksApi(perfCycleState.id, positionState.id);
    if (response.success) {
      setData(response.data || { blocking: [], waiting: [], resolved: [] });
    }
    setLoading(false);
  }

  function handleComplete(taskId) {
    setConfirmModal({ visible: true, taskId });
  }

  async function confirmComplete() {
    const res = await CompleteTaskApi(confirmModal.taskId);
    if (res.success) {
      openNotificationSuccess("bottomRight", "Task marked as completed");
      fetchDependencies();
    } else {
      openNotificationError("bottomRight", "Couldn't complete task");
    }
  }

  if (loading) return <PageLoader />;

  // Split waiting dependent tasks: only show pending ones in "waiting"
  const waitingDeps = [];
  (data.waiting || []).forEach(item => {
    (item.dependentTasks || []).forEach(dep => {
      if (!dep.completionStatus) {
        waitingDeps.push({ ...dep, parentTaskName: item.name });
      }
    });
  });

  // Split blocking tasks: only show pending ones in "blocking"
  const blockingTasks = [];
  (data.blocking || []).forEach(item => {
    (item.myBlockingTaskNames || []).forEach(myTask => {
      if (!myTask.completionStatus) {
        blockingTasks.push({ ...myTask, item });
      }
    });
  });

  const totalBlocking = blockingTasks.length;
  const totalWaiting = waitingDeps.length;
  // Resolved items come entirely from the backend now (includes released held tasks + completed dependency tasks)
  const allResolved = data.resolved || [];
  const totalResolved = allResolved.length;

  return (
    <div style={{ maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0 }}>
            Dependencies — {userName}
          </h2>
          <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>
            {userDesignation ? `${userDesignation} ` : ""}— dependencies on other leaders and teams
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Select value={selectedYear} onChange={(val) => { setSelectedYear(val); const max = moment().year(val).isoWeeksInYear(); if (selectedWeek > max) setSelectedWeek(max); }} size="small" style={{ width: 90 }}>
            {yearOptions.map(y => <Option key={y} value={y}>{y}</Option>)}
          </Select>
          <Select value={selectedWeek} onChange={(val) => setSelectedWeek(val)} size="small" style={{ width: 220 }} showSearch optionFilterProp="label">
            {weekOptions.map(opt => (
              <Option key={opt.value ?? "all"} value={opt.value} label={opt.label}>
                <span style={{ color: opt.value && weeksWithData.has(opt.value) ? "#0f172a" : "#94a3b8", fontWeight: opt.value && weeksWithData.has(opt.value) ? 700 : 400 }}>
                  {opt.label} {opt.value && weeksWithData.has(opt.value) ? "●" : ""}
                </span>
              </Option>
            ))}
          </Select>
          {totalBlocking > 0 && (
            <div style={{ padding: "5px 14px", borderRadius: 6, backgroundColor: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: 12, fontWeight: 700 }}>
              {totalBlocking} blocking others
            </div>
          )}
          {totalWaiting > 0 && (
            <div style={{ padding: "5px 14px", borderRadius: 6, backgroundColor: "#fffbeb", border: "1px solid #fde68a", color: "#d97706", fontSize: 12, fontWeight: 700 }}>
              {totalWaiting} pending
            </div>
          )}
        </div>
      </div>

      {/* Three-column layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18, alignItems: "flex-start" }}>

        {/* RED — I'm blocking these leaders */}
        <div style={{ backgroundColor: "#fff5f5", border: "1px solid #fed7d7", borderRadius: 12, padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: "#dc2626" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#991b1b" }}>I'm blocking these leaders</span>
            </div>
            <Tag color="red" style={{ margin: 0, fontWeight: 700 }}>{totalBlocking}</Tag>
          </div>

          {totalBlocking === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8", fontSize: 12 }}>
              <CheckCircleOutlined style={{ fontSize: 24, marginBottom: 6, display: "block" }} />
              No tasks blocking others
            </div>
          ) : (
            blockingTasks.map((bt, idx) => (
              <div key={`block-${idx}`} style={{ backgroundColor: "#fff", border: "1px solid #fecaca", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                {/* ── Leader being blocked — their avatar + name + designation ── */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  {bt.item.ownerImageUrl ? (
                    <img src={bt.item.ownerImageUrl} alt={bt.item.ownerName}
                      style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "#0f172a",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                      {(`${bt.item.ownerName || ""} ${bt.item.ownerLastName || ""}`).trim().split(" ").filter(Boolean).map(n => n[0].toUpperCase()).slice(0, 2).join("")}
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                      {bt.item.ownerName}{bt.item.ownerLastName ? ` ${bt.item.ownerLastName}` : ""}{bt.item.ownerDesignation ? ` · ${bt.item.ownerDesignation}` : ""}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>is being blocked by you</div>
                  </div>
                </div>

                {/* ── Their on-hold task ── */}
                <div style={{ backgroundColor: "#fff5f5", borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#991b1b", marginBottom: 4 }}>Their on-hold task</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 2 }}>{bt.item.name}</div>
                  {bt.item.planTitle && <div style={{ fontSize: 11, color: "#94a3b8" }}>Plan: {bt.item.planTitle}</div>}
                  {bt.item.daysOnHold > 0 && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>On hold {bt.item.daysOnHold}d</div>}
                </div>

                {/* ── My task that's blocking them ── */}
                <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>Complete your task to unblock them:</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#dc2626", marginBottom: 2 }}>{bt.name}</div>
                {bt.taskEndDate && (
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>Due: {moment(bt.taskEndDate).format("DD MMM YYYY")}</div>
                )}
                {bt.completionStatus ? (
                  <span style={{ fontSize: 11, backgroundColor: "#dcfce7", color: "#166534", padding: "3px 10px", borderRadius: 4, fontWeight: 700, border: "1px solid #86efac" }}>✓ Completed</span>
                ) : bt.item?.onHold ? (
                  <div>
                    <span style={{ fontSize: 11, backgroundColor: "#fef3c7", color: "#92400e", padding: "3px 10px", borderRadius: 4, fontWeight: 700, border: "1px solid #fde68a" }}>On Hold — cannot complete</span>
                    <div style={{ fontSize: 10, color: "#92400e", marginTop: 4 }}>This task is on hold waiting on other tasks first.</div>
                  </div>
                ) : (
                  <Button size="small" type="primary" onClick={() => handleComplete(bt.id)}
                    style={{ backgroundColor: "#059669", borderColor: "#059669", fontSize: 11, fontWeight: 600, borderRadius: 6 }}>
                    <CheckCircleOutlined /> Mark completed
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        {/* AMBER — Waiting on these leaders */}
        <div style={{ backgroundColor: "#fffaf0", border: "1px solid #fde68a", borderRadius: 12, padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: "#d97706" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>Waiting on these leaders</span>
            </div>
            <Tag color="orange" style={{ margin: 0, fontWeight: 700 }}>{totalWaiting}</Tag>
          </div>

          {totalWaiting === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8", fontSize: 12 }}>
              <CheckCircleOutlined style={{ fontSize: 24, marginBottom: 6, display: "block" }} />
              Not waiting on anyone
            </div>
          ) : (
            waitingDeps.map((depTask, idx) => (
              <div key={`wait-${idx}`} style={{ backgroundColor: "#fff", border: "1px solid #fde68a", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", fontFamily: "monospace" }}>
                    {depTask.ownerName?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{depTask.ownerName}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{depTask.ownerDesignation}</div>
                  </div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>{depTask.name}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Plan: {depTask.planTitle}</div>
                <div style={{ fontSize: 11, color: "#d97706", marginBottom: 10 }}>Blocking my task: "{depTask.parentTaskName}"</div>
                {depTask.completionStatus ? (
                  <span style={{ fontSize: 11, backgroundColor: "#dcfce7", color: "#166534", padding: "3px 10px", borderRadius: 4, fontWeight: 700, border: "1px solid #86efac" }}>✓ Completed</span>
                ) : depTask.onHold ? (
                  <div>
                    <span style={{ fontSize: 11, backgroundColor: "#fef3c7", color: "#92400e", padding: "3px 10px", borderRadius: 4, fontWeight: 700, border: "1px solid #fde68a" }}>On Hold — cannot complete</span>
                    <div style={{ fontSize: 10, color: "#92400e", marginTop: 4 }}>Waiting on its own dependencies to finish first.</div>
                  </div>
                ) : (
                  <Button size="small" type="primary" onClick={() => handleComplete(depTask.id)} style={{ backgroundColor: "#059669", borderColor: "#059669", fontSize: 11, fontWeight: 600, borderRadius: 6 }}>
                    <CheckCircleOutlined /> Mark completed
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        {/* GREEN — Resolved this week */}
        <div style={{ backgroundColor: "#f0fff4", border: "1px solid #86efac", borderRadius: 12, padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: "#16a34a" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>Resolved this week</span>
            </div>
            <Tag color="green" style={{ margin: 0, fontWeight: 700 }}>{totalResolved}</Tag>
          </div>

          {totalResolved === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8", fontSize: 12 }}>
              <WarningOutlined style={{ fontSize: 24, marginBottom: 6, display: "block" }} />
              No resolved dependencies this week
            </div>
          ) : (
            (() => {
              // The API returns resolved entries pre-grouped:
              // - released/manual entries have a dependencyTasks array
              // - orphan dependency entries (no parent) are standalone
              const groups = [];

              allResolved.forEach(task => {
                if (task.resolveType === "released" || task.resolveType === "manual") {
                  groups.push({ heldTask: task, deps: task.dependencyTasks || [] });
                } else if (task.resolveType === "dependency") {
                  // Orphan dep — no held task parent found, show as its own group
                  groups.push({ heldTask: { name: task.releasedHeldTask || "Unknown", resolveType: "released", completionStatus: false, planTitle: task.planTitle, resolvedOn: task.resolvedOn, daysToResolve: null, ownerName: null, ownerLastName: null }, deps: [task] });
                }
              });

              return groups.map((group, idx) => (
                <ResolvedGroup key={group.heldTask.id || group.heldTask.name || idx} group={group} />
              ));
            })()
          )}
        </div>
      </div>

      <ConfirmationModal
        visible={confirmModal.visible}
        title="Complete Task"
        text="Are you sure you want to mark this task as completed?"
        handleOk={confirmComplete}
        handleCancel={() => setConfirmModal({ visible: false, taskId: null })}
      />
    </div>
  );
}
